'use client'

// SUMMARY: Demo chat row — wraps the shared ChatMessage but condenses web-search sources into a click-to-expand list

import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { ChatMessage } from '@/components/chat/chat-message'
import type { ChatMessage as ChatMessageType } from '@/components/chat/use-chat'

// EXAMPLE: "...answer\n\n---\n**Quellen**\n1. [a](url)" → answer part + heading "Quellen" + link list
const SOURCES_PATTERN = /\n+---\n\*\*(Quellen|Sources)\*\*\n([\s\S]*)$/

interface SourceLink {
	title: string
	url: string
}

interface ParsedSources {
	answer: string
	heading: string
	sources: SourceLink[]
}

function parseSources(content: string): ParsedSources | null {
	const match = SOURCES_PATTERN.exec(content)
	if (!match) return null
	const sources: SourceLink[] = []
	for (const line of match[2].split('\n')) {
		// EXAMPLE: "1. [LCK - LoL Esports](https://lolesports.com/schedule)" → title "LCK - LoL Esports" + url
		const link = /^\d+\.\s+\[(.+?)\]\((.+?)\)\s*$/.exec(line.trim())
		if (link) sources.push({ title: link[1], url: link[2] })
	}
	if (sources.length === 0) return null
	return { answer: content.slice(0, match.index), heading: match[1], sources }
}

export function DemoChatMessage({ message }: { message: ChatMessageType }) {
	const [expanded, setExpanded] = useState(false)
	// Only split once the answer finished streaming — mid-stream the sources block is still arriving
	const parsed = message.role === 'assistant' && !message.streaming ? parseSources(message.content) : null

	if (!parsed) return <ChatMessage message={message} />

	return (
		<div className="space-y-1">
			<ChatMessage message={{ ...message, content: parsed.answer }} />
			{/* pl-11 aligns the toggle under the bubble (h-8 avatar + gap-3) */}
			<div className="pl-11">
				<button
					onClick={() => setExpanded(!expanded)}
					className="flex cursor-pointer items-center gap-1 py-2 -my-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
				>
					<ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
					{parsed.heading} ({parsed.sources.length})
				</button>
				{expanded && (
					<ol className="mt-1.5 space-y-1 pl-8 text-xs">
						{parsed.sources.map((source, i) => (
							<li key={i} className="list-decimal text-muted-foreground">
								<a
									href={source.url}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:underline dark:text-blue-400"
								>
									{source.title}
								</a>
							</li>
						))}
					</ol>
				)}
			</div>
		</div>
	)
}
